import React, { Component } from 'react';
import { MenuItem, DropdownButton } from 'react-bootstrap';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import DataList from 'components/ui/DataList';
import RolesPermissionsModal from 'components/modules/settings/roles-permissions-modal';
import SessionStore from 'stores/SessionStore';
import CargomaticCard from 'components/v2/card';
import { Popover, Transition } from '@headlessui/react';
import CargomaticInput from 'components/v2/input';
import LoadingCircle from 'components/ui/LoadingCircle';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import DownloadIcon from 'components/v2/icons/DownloadIcon';
import { exportUsersCsv } from 'utils/UserWebAPIUtils';
import { showToast } from 'stores/ToastStore';
import fileDownload from 'react-file-download';
import RegisterUserForm from './RegisterUserForm';
import UserActionCreators from '../../../actions/UserActionCreators';
import UserStore from '../../../stores/UserStore';
import AdminMapping from './AdminMapping';

const PER_PAGE = 25;
const PAGE_ITEMS_PER_PAGE = 5;

const STATUS_SORT_ITEMS = [
  {
    label: 'Active',
    value: 'active',
    localizationSlug: 'ACTIVE',
  },
  {
    label: 'Deactivated',
    value: 'deactivated',
    localizationSlug: 'DEACTIVATED',
  },
];

const getInitialState = () => {
  return {
    showModal: false,
    showRolesModal: false,
    selected: [],
    users: [],
    page: 1,
    pages: 0,
    total: 0,
    active: true,
    deactivated: true,
    search: '',
    loading: false,
    exportLoading: false,
    userColumns: AdminMapping.columns,
    filters: {
      status: ['active', 'deactivated'],
    },
  };
};

class AdminUserList extends Component {
  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  componentDidMount() {
    UserStore.addChangeListener(this.handleUserStoreChange);
    this.getAdminUsers();
  }

  componentWillUnmount() {
    UserStore.removeChangeListener(this.handleUserStoreChange);
  }

  getSelectedUsers() {
    return this.state.users.filter((u) => this.state.selected.includes(u._id));
  }

  handleUserStoreChange = () => {
    const data = UserStore.getAdminUsers() || [];
    const { page, pages, total, users } = data[0];
    const structuredUsers = users.map((u) => {
      const first_name = (u.first_name || '').trim();
      const last_name = (u.last_name || '').trim();
      const name = `${first_name} ${last_name}`.trim();

      return {
        ...u,
        first_name,
        last_name,
        name,
      };
    });
    this.setState((prev) => {
      return {
        loading: false,
        users: structuredUsers,
        page,
        pages,
        total,
      };
    });
  };

  handleSelectRow = (row, selected) => {
    const { selected: selectedRows } = this.state;

    if (!selected) {
      const index = selectedRows.indexOf(row._id);
      this.setState({
        selected: [
          ...selectedRows.slice(0, index),
          ...selectedRows.slice(index + 1),
        ],
      });
    } else {
      this.setState({
        selected: [...selectedRows, row._id],
      });
    }
  };

  getFilterParams = () => {
    const { page, active, deactivated, search } = this.state;
    const params = {
      page,
      per_page: PER_PAGE,
      paginate: true,
    };
    if (search.length > 0) {
      params.search = search;
    }
    if (active !== deactivated) {
      params.active = active;
    }
    return params;
  };

  getAdminUsers = () => {
    const params = this.getFilterParams();
    UserActionCreators.getAdminUsers(params);
    this.setState((prev) => {
      return {
        loading: !prev.loading,
      };
    });
  };

  renderSortByStatus = () => {
    const { t } = this.props;
    return (
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button className="relative button button-green">
              <span className="text-sm">{t('FILTER_BY_STATUS')}</span>
            </Popover.Button>
            <Popover.Overlay />
            <Transition
              show={open}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel
                static
                className="absolute left-0 z-10 h-auto max-w-sm px-4 mt-3 sm:px-0"
              >
                <div className="rounded-lg shadow-lg p-2 bg-white">
                  {STATUS_SORT_ITEMS.map((item) => (
                    <CargomaticInput
                      className="m-2"
                      id={item.value}
                      key={item.value}
                      label={titleize(t(item.localizationSlug))}
                      type="checkbox"
                      checked={this.state[item.value]}
                      onChange={() => this.handleSortStatusChange(item.value)}
                    />
                  ))}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  handleSortStatusChange = (value) => {
    this.setState(
      (prev) => {
        const filters = {
          status: prev.filters.status.includes(value)
            ? prev.filters.status.filter((v) => v !== value)
            : prev.filters.status.concat(value),
        };
        return {
          ...prev,
          page: 1,
          [value]: !prev[value],
          filters,
        };
      },
      () => {
        this.getAdminUsers();
      },
    );
  };

  handleDeactivate = () => {
    this.setState((prev) => ({
      showDeactivateConfirmationModal: false,
    }));
    const params = this.getFilterParams();
    const ids = this.getSelectedUsers().map((user) => user._id);
    UserActionCreators.deactivateUsers(ids, params);
  };

  handleActivate = () => {
    this.setState((prev) => ({
      showActivateConfirmationModal: false,
    }));
    const params = this.getFilterParams();
    const ids = this.getSelectedUsers().map((user) => user._id);
    UserActionCreators.activateUsers(ids, params);
  };

  deactivateConfirmationMessage() {
    const { t } = this.props;
    const users = this.getSelectedUsers();
    const names = users.map((user) => user.name);
    return t('CONFIRM_USER_DEACTIVATION', { names: names.join(', ') });
  }

  activateConfirmationMessage() {
    const { t } = this.props;
    const users = this.getSelectedUsers();
    const names = users.map((user) => user.name);
    return t('CONFIRM_USER_ACTIVATION', { names: names.join(', ') });
  }

  handleSelectAll = (selected, rows) => {
    if (!selected) {
      this.setState({
        selected: [],
      });
    } else {
      this.setState({ selected: rows.map((r) => r._id) });
    }
  };

  close() {
    this.setState({ showModal: false });
  }

  open() {
    this.setState({ showModal: true });
  }

  canPerformActionOnUsers() {
    const user = SessionStore.getUser();
    return user.roles.some((role) =>
      ['admin', 'internal_account_administrator'].includes(role),
    );
  }

  canActivate() {
    if (this.state.selected.length === 0) return false;
    return this.canPerformActionOnUsers();
  }

  canDeactivate() {
    if (this.state.selected.length === 0) return false;
    return this.canPerformActionOnUsers();
  }

  canChangeRoles() {
    if (this.state.selected.length === 0) return false;
    return SessionStore.hasAdminRole();
  }

  canRegisterNewUser() {
    const user = SessionStore.getUser();
    return user.roles.some((role) =>
      ['admin', 'internal_account_administrator'].includes(role),
    );
  }

  handleRolesSave(users) {
    const params = this.getFilterParams();
    this.setState(
      (prev) => ({
        showRolesModal: false,
      }),
      () => {
        UserActionCreators.updateUsersRoles(users, params);
      },
    );
  }

  handlePageClick = (selectedPage) => {
    console.log(selectedPage);
    this.setState({ page: selectedPage }, () => {
      this.getAdminUsers();
    });
  };

  handleSearch = (text) => {
    this.setState(
      (prev) => {
        return {
          ...prev,
          search: text,
          page: 1,
        };
      },
      () => {
        this.getAdminUsers();
      },
    );
  };

  exportCsv = () => {
    const { t } = this.props;
    this.setState({ exportLoading: true }, async () => {
      const { userColumns: columns, filters } = this.state;
      try {
        const { entity } = await exportUsersCsv(columns, filters);
        fileDownload(entity, 'registered_users.csv');
      } catch (e) {
        showToast(t('EXPORT_CSV'), t('CSV_EXPORT_REQUEST_FAILED'), 'error');
      } finally {
        this.setState({ exportLoading: false });
      }
    });
  };
  render() {
    const { t } = this.props;

    const columns = AdminMapping.columns;

    return (
      <CargomaticCard
        header={() => <h3 className="my-0">{t('ADMIN_USERS')}</h3>}
      >
        <div className="flex mb-5 justify-between items-center flex-wrap">
          <div className="flex justiy-end gap-x-4">
            <button
              type="button"
              disabled={!this.canRegisterNewUser()}
              className="button button-green"
              onClick={() => this.open()}
            >
              {t('REGISTER_NEW_USER')}
            </button>
            <div>{this.renderSortByStatus()}</div>
            <DropdownButton
              id="actions"
              className="button"
              bsStyle="danger"
              title={titleize(t('ACTIONS'))}
            >
              <MenuItem
                disabled={!this.canActivate()}
                onSelect={() => {
                  this.setState({ showActivateConfirmationModal: true });
                }}
              >
                {titleize(t('ACTIVE'))}
              </MenuItem>
              <MenuItem
                disabled={!this.canDeactivate()}
                onSelect={() => {
                  this.setState({ showDeactivateConfirmationModal: true });
                }}
              >
                {titleize(t('DEACTIVATED'))}
              </MenuItem>
              <MenuItem
                disabled={!this.canChangeRoles()}
                onSelect={() => this.setState({ showRolesModal: true })}
              >
                {t('ROLES_AND_PERMISSIONS')}
              </MenuItem>
            </DropdownButton>
            {this.canPerformActionOnUsers() && (
              <button
                type="button"
                className="button button-green"
                disabled={this.state.exportLoading}
                onClick={this.exportCsv}
              >
                {this.state.exportLoading ? (
                  <LoadingCircle />
                ) : (
                  <div className="flex items-center space-x-1">
                    <span>{t('EXPORT')}</span> <DownloadIcon />
                  </div>
                )}
              </button>
            )}
          </div>
          <CargomaticInput
            className="w-80 mb-2"
            type=""
            placeholder={t('SEARCH_BY_NAME_OR_EMAIL_ID')}
            onChange={(search) => this.handleSearch(search)}
          />
        </div>
        {this.state.loading && <LoadingCircle />}
        {!this.state.loading && (
          <DataList
            showHeaders
            select
            hover
            striped
            paginate
            perPage={PER_PAGE}
            page={this.state.page}
            pages={this.state.pages}
            total={this.state.total}
            onPaginationSelect={this.handlePageClick}
            data={this.state.users}
            columns={columns}
            selected={this.state.selected}
            onSelect={(row, selected) => this.handleSelectRow(row, selected)}
            onSelectAll={(selected, rows) =>
              this.handleSelectAll(selected, rows)
            }
          />
        )}
        <RegisterUserForm
          show={this.state.showModal}
          onHide={() => this.close()}
        />
        <ConfirmModal
          message={this.deactivateConfirmationMessage()}
          title={t('ARE_YOU_SURE')}
          onConfirm={() => this.handleDeactivate()}
          show={this.state.showDeactivateConfirmationModal}
          onHide={() => {
            this.setState({ showDeactivateConfirmationModal: false });
          }}
        />
        <ConfirmModal
          message={this.activateConfirmationMessage()}
          title={t('ARE_YOU_SURE')}
          onConfirm={() => this.handleActivate()}
          show={this.state.showActivateConfirmationModal}
          onHide={() => this.setState({ showActivateConfirmationModal: false })}
        />
        {this.state.showRolesModal && (
          <RolesPermissionsModal
            show={this.state.showRolesModal}
            onHide={() => this.setState({ showRolesModal: false })}
            userIds={this.state.selected}
            onSave={(users) => this.handleRolesSave(users)}
          />
        )}
      </CargomaticCard>
    );
  }
}

export default withTranslation()(AdminUserList);
